import { formatDate as _formatDate, parse as _parseDate } from 'date-fns'

/**
 * システム日付を取得
 *
 * @returns 日付
 */
export const getSystemDatetime = () => {
  return new Date(Date.now() + (new Date().getTimezoneOffset() + 9 * 60) * 60 * 1000)
}

/**
 * 日付変換（YYYYMMDDhhmmss）
 *
 * @param {String} strDateTime 日付文字列
 * @returns 日付
 */
export const getDatetimeByYmdhms = (strDateTime) => {
  const year = parseInt(strDateTime.substring(0, 4))
  const month = parseInt(strDateTime.substring(4, 6))
  const day = parseInt(strDateTime.substring(6, 8))
  const hour = strDateTime.substring(8, 10)
  const min = strDateTime.substring(10, 12)
  const sec = strDateTime.substring(12, 14)
  return new Date(year, month - 1, day, hour, min, sec)
}

/**
 * 日付変換（YYYYMMDD）
 *
 * @param {String} strDateTime 日付文字列
 * @returns 日付
 */
export const getDateByYmd = (strDate) => {
  const year = parseInt(strDate.substring(0, 4))
  const month = parseInt(strDate.substring(4, 6))
  const day = parseInt(strDate.substring(6, 8))
  return new Date(year, month - 1, day)
}

/**
 * 日付文字列（YYYYMMDD、YYYY-MM-DD、YYYY/MM/DDなど）をDateオブジェクトに変換
 *
 * @param {string} strDate - 日付文字列（例: "20230401", "2023-04-01", "2023/04/01"）
 * @returns {Date | null} 変換に成功したDateオブジェクト。失敗時はnull。
 */
export const parseDateString = (strDate) => {
  if (typeof strDate !== 'string') return null

  let year, month, day

  // 区切り文字がない場合（例: 20230401）
  if (/^\d{8}$/.test(strDate)) {
    year = parseInt(strDate.substring(0, 4), 10)
    month = parseInt(strDate.substring(4, 6), 10)
    day = parseInt(strDate.substring(6, 8), 10)

    // 区切り文字がある場合（例: 2023-04-01 または 2023/04/01）
  } else if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(strDate)) {
    const parts = strDate.split(/[-\/]/)
    year = parseInt(parts[0], 10)
    month = parseInt(parts[1], 10)
    day = parseInt(parts[2], 10)
    // その他の形式はDateコンストラクタで処理
  } else {
    const date = new Date(strDate)
    if (isNaN(date.getTime())) return null
    return date
  }

  // Dateオブジェクト生成
  const date = new Date(year, month - 1, day)

  // 不正な日付チェック
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }

  return date
}

/**
 * 日付フォーマット変換（YYYYMMDD）
 *
 * @param {String} strDateTime 日付文字列
 * @returns 日付文字列（YYYY/MM/DD）
 */
export const formatDateYmd = (strDate) => {
  return strDate.substring(0, 4) + '/' + strDate.substring(4, 6) + '/' + strDate.substring(6, 8)
}

/**
 * 日付フォーマット変換（HHMMSS）
 *
 * @param {String} strDateTime 日付文字列
 * @returns 変換後文字列（HH:MM:SS）
 */
export const formatDateHHmmdd = (date) => {
  let format_str = 'hh:mm:ss'
  format_str = format_str.replace(/hh/g, date.getHours().toString().padStart(2, '0'))
  format_str = format_str.replace(/mm/g, date.getMinutes().toString().padStart(2, '0'))
  format_str = format_str.replace(/ss/g, date.getSeconds().toString().padStart(2, '0'))
  return format_str
}

/**
 * 日付フォーマット
 *
 * @param {Date} date 日付
 * @returns 変換後文字列（YYYY/MM/DD hh:mm:ss）
 */
export const formatDate = (date) => {
  let format_str = 'YYYY/MM/DD hh:mm:ss'
  format_str = format_str.replace(/YYYY/g, date.getFullYear().toString())
  format_str = format_str.replace(/MM/g, (date.getMonth() + 1).toString().padStart(2, '0'))
  format_str = format_str.replace(/DD/g, date.getDate().toString().padStart(2, '0'))
  format_str = format_str.replace(/hh/g, date.getHours().toString().padStart(2, '0'))
  format_str = format_str.replace(/mm/g, date.getMinutes().toString().padStart(2, '0'))
  format_str = format_str.replace(/ss/g, date.getSeconds().toString().padStart(2, '0'))
  return format_str
}

/**
 * 数値形式に変換（"/"(スラッシュ)、":"(コロン)、" "(空白スペース)を削除）
 *
 * @param {String} date 日付文字列
 * @returns 変換後文字列
 */
export const formatDateNumber = (date) => {
  const reg = new RegExp(/\/|:|\s/, 'g')
  return date.replace(reg, '')
}

/**
 * 形式変換（0を削除）
 *
 * @param {String} date 日付文字列
 * @returns 変換後文字列
 */
export const formatStringYm = (date) => {
  const [year, month] = date.split('/').map(Number)
  return `${year}/${month}`
}

/**
 * 形式変換（スラッシュ区切りを年月日に変換） null / undefined / 空文字の場合は空文字を返す（EndDate 未設定の組織階層などに対応）
 *
 * @param {String | null | undefined} dateStr 日付文字列（YYYY/MM/DD）
 * @returns 変換後文字列
 */
export const convertDateToJapaneseFormat = (dateStr) => {
  if (dateStr == null || typeof dateStr !== 'string' || dateStr.trim() === '') {
    return ''
  }
  // 日付文字列を分割して年、月、日を取得
  const parts = dateStr.split('/')
  const year = parts[0]
  const month = parts[1]
  const day = parts[2]

  // 日本語の年月日形式に変換
  return `${year}年${month}月${day}日`
}

/**
 * 形式変換（日付文字列を「YYYY年M月」形式に変換）
 *
 * @param {string | null | undefined} dateString - フォーマット対象の日付文字列（例: "2024-04-01"）
 * @returns {string} フォーマット済みの年月文字列（例: "2024年4月"）
 */
export function formatDateToYearMonth(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)
  if (isNaN(date)) return ''

  const year = date.getFullYear()
  const month = date.getMonth() + 1

  return `${year}年${month}月`
}

/**
 * 日付を月単位表示（yyyy/MM）に変換 画面表示用。Date または ISO/yyyy-MM-dd 文字列に対応。
 *
 * @param {Date | string | null | undefined} date - 日付
 * @returns {string} Yyyy/MM 形式、無効または空の場合は空文字
 */
export function formatDateAsMonth(date) {
  if (!date) return ''
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  return `${y}/${String(m).padStart(2, '0')}`
}

/**
 * 指定月の月初日（1日）の Date を返す
 *
 * @param {number} year - 年
 * @param {number} month - 月（1-12）
 * @returns {Date}
 */
export function getFirstDayOfMonth(year, month) {
  return new Date(year, month - 1, 1)
}

/**
 * 指定月の月末日の Date を返す
 *
 * @param {number} year - 年
 * @param {number} month - 月（1-12）
 * @returns {Date}
 */
export function getLastDayOfMonth(year, month) {
  return new Date(year, month, 0)
}

/** @function normalize 日付に0を埋める */
export const normalize = (date) =>
  String(date).replace(/^(\d{4})(\D{1,2})(\d{1,2})(\D{1,2})(\d{1,2})$/g, (_, y, sep, m, sep2, d) =>
    [y, sep, String(m).padStart(2, '0'), sep2, String(d).padStart(2, '0')].join('')
  )
/**
 * @function formatDateCustom 日付の形式を変換
 * @param {Date | string} date
 * @param {string} format
 * @returns {string}
 */
export const formatDateCustom = (date, format = 'yyyy/MM/dd') => (!date ? '' : _formatDate(normalize(date), format))

let refDate = null
/**
 * @function parseDateCustom 日付をDateに変換
 * @param {string} date
 * @param {string} format
 * @param {Date} ref
 */
export const parseDateCustom = (date, format = 'yyyy-MM-dd', ref) => {
  if (!date) return null
  if (refDate === null && !ref) refDate = new Date()
  return _parseDate(normalize(date), format, ref ?? refDate)
}

/** Test-only: reset the cached reference date between tests. Must NOT be called from production code. */
export const __resetDateUtilsCache = () => {
  refDate = null
}
